import React from 'react';

/**
 * Sprint 12 — ErrorBoundary global. Cierra C12 parcialmente:
 *  - Cuando un componente lanza durante render, en lugar de pantalla en blanco
 *    mostramos un fallback amigable con opción de reiniciar o reportar.
 *  - Loguea el error a `window.api.error.reportar` (no-op si no hay sesión).
 *
 * NO captura errores de promesas await (esos los maneja apiSafe + try/catch).
 */
interface State {
  error: Error | null;
}

interface Props {
  children: React.ReactNode;
}

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // No bloqueamos si el reporter no está disponible.
    try {
      window.api?.error?.reportar?.(
        `[ErrorBoundary] ${error.message}`,
        `componentStack:${info.componentStack?.slice(0, 1000) ?? 'unknown'}`,
      );
    } catch {
      /* swallow */
    }
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info);
  }

  private reiniciar = (): void => {
    // Recarga la app dentro del renderer. No es relaunch del main, es
    // suficiente para limpiar el árbol React tras un crash.
    window.location.reload();
  };

  private reset = (): void => {
    this.setState({ error: null });
  };

  render(): React.ReactNode {
    if (!this.state.error) return this.props.children;

    const detalle = this.state.error.stack ?? this.state.error.message;
    return (
      <div className="flex h-full min-h-screen items-center justify-center bg-slate-100 p-4">
        <div className="w-full max-w-xl rounded-xl bg-white p-6 shadow-xl">
          <div className="mb-4 flex items-start gap-3">
            <span className="text-4xl text-red-600" aria-hidden>
              ⚠
            </span>
            <div className="flex-1">
              <h1 className="text-xl font-bold text-slate-900">Algo salió mal</h1>
              <p className="mt-2 text-sm text-slate-700">
                Detectamos un error inesperado en la aplicación. Tu información no se ha perdido —
                puedes intentar reanudar o reiniciar.
              </p>
            </div>
          </div>
          <details className="mb-4 text-xs text-slate-500">
            <summary className="cursor-pointer">Detalles técnicos</summary>
            <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap rounded bg-slate-50 p-2 text-[11px]">
              {detalle}
            </pre>
          </details>
          <div className="flex flex-wrap justify-end gap-2">
            <button
              type="button"
              className="min-h-[44px] rounded-lg border border-slate-300 bg-white px-5 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
              onClick={this.reset}
            >
              Intentar de nuevo
            </button>
            <button
              type="button"
              className="min-h-[44px] rounded-lg bg-primary px-5 py-2 text-sm font-medium text-white hover:opacity-90"
              onClick={this.reiniciar}
            >
              Reiniciar la app
            </button>
          </div>
        </div>
      </div>
    );
  }
}
